import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { IExperiment } from './experimentApi.types';
import { ResultState } from './resultApi.types';

export function utf8ToBase64(utf8String: string): string {
    const byteArray = new TextEncoder().encode(utf8String);

    // Convert the byte array to a string of binary data
    const binaryString = Array.from(byteArray)
        .map((byte) => String.fromCharCode(byte))
        .join('');

    // Convert the binary string to a Base64 string
    return btoa(binaryString);
}

export function base64ToUtf8(base64String: string): string {
    // Normalize base64url to standard base64 (handles JWT tokens)
    let normalized = base64String.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4;
    if (padding === 1) {
        throw new Error('Invalid base64 string length');
    } else if (padding === 2) {
        normalized += '==';
    } else if (padding === 3) {
        normalized += '=';
    }

    const binaryString = atob(normalized);
    const byteArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
        byteArray[i] = binaryString.charCodeAt(i);
    }

    return new TextDecoder('utf-8', { fatal: true }).decode(byteArray);
}

export function parseApiErrorMessage(error: FetchBaseQueryError | SerializedError): string {
    try {
        // SerializedError
        if ('message' in error) {
            return error.message;
        }

        if ('originalStatus' in error && error.originalStatus === 404) {
            return 'Not found';
        }

        // FetchBaseQueryError
        const { data } = error as FetchBaseQueryError;
        if (typeof data === 'string') {
            return data;
        }

        if (typeof data === 'object' && 'detail' in data && typeof data.detail === 'string') {
            return data.detail;
        }
        // Attempt to extract the first value from the data object
        // This is for errors that are returned as an object with a single key but,
        // since the key is dynamic, we can't know it
        // TODO: This is a workaround and should be removed once the API is updated
        const values = Object.values(data);
        if (Array.isArray(values) && values.length > 0) {
            return values[0];
        }

        // Fallback
        return JSON.stringify(error);
    } catch (error) {
        console.error('Error parsing API error message: ', error);
        return 'Unknown error';
    }
}

export function getResultStateFromExperiment(experiment: IExperiment): ResultState {
    const { result_counts } = experiment;

    if (experiment.uuid) {
        return result_counts.in_progress > 0 ? ResultState.pending : ResultState.completed;
    }

    if (result_counts.in_progress > 0) {
        return ResultState.pending;
    }

    if (result_counts.cancelled > 0) {
        return ResultState.cancelled;
    }

    if (result_counts.failed > 0) {
        return ResultState.error;
    }

    return ResultState.completed;
}
